import { directSupabase } from '../lib/supabaseClient';
import { loadIdentityIndex } from './identity.client';
import { normalizeUkDigits } from './phone';
import { otherSupplierWithPhone, supplierAlreadyHasPhone } from './suppliers.phones';

export type SupplierPickerRow = {
  id: string;
  name: string;
  phone: string | null;
};

function missingSupplierPhonesTable(error: { message?: string; code?: string } | null): boolean {
  const text = String(error?.message || '');
  return error?.code === '42P01' || text.includes('supplier_phones');
}

export async function listSuppliersForPicker(): Promise<SupplierPickerRow[]> {
  const { data, error } = await directSupabase
    .from('suppliers')
    .select('id, supplier_name, phone')
    .order('supplier_name', { ascending: true });
  if (error) throw new Error(error.message);
  return (data || []).map((row) => ({
    id: String(row.id),
    name: String(row.supplier_name || '').trim() || 'Supplier',
    phone: row.phone ? String(row.phone) : null,
  }));
}

export async function attachPhoneToSupplier(input: {
  supplierId: string;
  phone: string;
  label?: string | null;
}): Promise<{ supplierName: string; alreadyHad: boolean }> {
  const original = String(input.phone || '').trim();
  if (!original) throw new Error('Telephone number is required.');

  const { data: supplier, error: supplierError } = await directSupabase
    .from('suppliers')
    .select('id, supplier_name, phone')
    .eq('id', input.supplierId)
    .single();
  if (supplierError || !supplier) throw new Error('Supplier not found.');

  const extras = await directSupabase
    .from('supplier_phones')
    .select('phone_original, phone_normalized')
    .eq('supplier_id', input.supplierId);

  const existing = [
    supplier.phone,
    ...((extras.error && missingSupplierPhonesTable(extras.error))
      ? []
      : (extras.data || []).flatMap((row) => [row.phone_original, row.phone_normalized])),
  ];
  if (supplierAlreadyHasPhone(existing, original)) {
    return { supplierName: supplier.supplier_name, alreadyHad: true };
  }

  const index = await loadIdentityIndex();
  const other = otherSupplierWithPhone(index.suppliers, input.supplierId, original);
  if (other) {
    throw new Error(`${other.name} already has this number.`);
  }

  const inserted = await directSupabase.from('supplier_phones').insert({
    supplier_id: input.supplierId,
    phone_original: original,
    phone_normalized: normalizeUkDigits(original),
    label: input.label?.trim() || null,
  });

  if (inserted.error && missingSupplierPhonesTable(inserted.error)) {
    if (!supplier.phone) {
      const updated = await directSupabase
        .from('suppliers')
        .update({ phone: original })
        .eq('id', input.supplierId);
      if (updated.error) throw new Error(updated.error.message);
      return { supplierName: supplier.supplier_name, alreadyHad: false };
    }
    throw new Error('Apply the supplier_phones SQL to store extra supplier numbers.');
  }
  if (inserted.error) {
    if (/idx_supplier_phones_supplier_normalized|duplicate key|23505/i.test(inserted.error.message || '')) {
      return { supplierName: supplier.supplier_name, alreadyHad: true };
    }
    throw new Error(inserted.error.message);
  }

  return { supplierName: supplier.supplier_name, alreadyHad: false };
}
